import React, { useState } from "react";
import { Copy, Check, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Step-by-step 2FA enrolment: shows the QR + secret, then collects the
 * 6-digit verification code. `setup` is the response from POST /auth/2fa/setup.
 */
const SetupWizard = ({ setup, lang, submitting, onVerify, onCancel }) => {
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard error");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length === 6) onVerify(code.trim());
  };

  return (
    <div className="space-y-6" data-testid="2fa-setup-pane">
      <div className="space-y-2">
        <p className="kw-label">
          {lang === "fr"
            ? "Étape 1 — Scannez le QR-code"
            : "Step 1 — Scan the QR code"}
        </p>
        <p className="text-xs text-[#A19E9C]">
          {lang === "fr"
            ? "Ouvrez Microsoft Authenticator → + → Scannez un code QR."
            : "Open Microsoft Authenticator → + → Scan a QR code."}
        </p>
      </div>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="bg-white p-3 inline-block">
          <img
            src={`data:image/png;base64,${setup.qr_png_b64}`}
            alt="2FA QR code"
            data-testid="2fa-qr-image"
            className="w-44 h-44 block"
          />
        </div>
        <div className="flex-1 space-y-3">
          <p className="kw-label">
            {lang === "fr" ? "Ou saisie manuelle" : "Or enter manually"}
          </p>
          <div className="flex items-center gap-2">
            <code
              className="font-mono text-sm text-[#F2EBE5] bg-[#050505] px-3 py-2 border border-[#F2EBE5]/10 select-all break-all"
              data-testid="2fa-secret-text"
            >
              {setup.secret}
            </code>
            <button
              type="button"
              onClick={copySecret}
              data-testid="2fa-copy-secret"
              className="text-[#A19E9C] hover:text-[#C5A059] p-2"
              title={lang === "fr" ? "Copier" : "Copy"}
            >
              {copied ? (
                <Check className="w-4 h-4 text-[#C5A059]" strokeWidth={2} />
              ) : (
                <Copy className="w-4 h-4" strokeWidth={1.5} />
              )}
            </button>
          </div>
          <p className="text-[10px] text-[#A19E9C] italic">
            {lang === "fr"
              ? "Compte : Kiwami Studios & Co · Type : TOTP (6 chiffres, 30s)"
              : "Account: Kiwami Studios & Co · Type: TOTP (6 digits, 30s)"}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-4 border-t border-[#F2EBE5]/10 pt-6"
        data-testid="2fa-verify-form"
      >
        <div>
          <p className="kw-label mb-3">
            {lang === "fr"
              ? "Étape 2 — Code de vérification"
              : "Step 2 — Verification code"}
          </p>
          <input
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            data-testid="2fa-verify-code"
            placeholder="123456"
            maxLength={6}
            className="kw-input font-mono tracking-widest w-48"
            autoFocus
          />
        </div>
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={submitting || code.length !== 6}
            data-testid="2fa-verify-submit"
            className="kw-btn-primary disabled:opacity-40"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                {lang === "fr" ? "Vérification…" : "Verifying…"}
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                {lang === "fr" ? "Activer" : "Activate"}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            data-testid="2fa-setup-cancel"
            className="kw-btn-ghost"
          >
            {lang === "fr" ? "Annuler" : "Cancel"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SetupWizard;
